import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import {
  LockOutlined as LockOutlinedIcon,
  PointOfSale as PointOfSaleIcon,
  Search as SearchIcon,
  Payments as PaymentsIcon,
  ReceiptLong as ReceiptLongIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import PrintListado, { buildTableHtml } from 'utils/PrintListado';

const PROGRAMA_CUADRE_CAJA_POS = 'cuadrecajapos';
const OPCION_TODAS_LOCALIDADES = { id: '', texto: 'Todas las localidades' };

export const accesoCuadreCajaPos = (roleDetalles) =>
  tienePermisosModulo(roleDetalles, PROGRAMA_CUADRE_CAJA_POS);

const hoyIso = () => new Date().toISOString().slice(0, 10);

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatearFecha = (fecha) => {
  if (!fecha) return '—';
  try {
    const d = String(fecha).includes('T') ? new Date(fecha) : new Date(`${fecha}T12:00:00`);
    return d.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const cellHeadDetalleSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const cellEllipsisSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const chipOrigen = (origen) => {
  const esContado = origen === 'factinv';
  return (
    <Chip
      size="small"
      label={esContado ? 'Venta contado' : 'Cobro CXC'}
      sx={{
        fontWeight: 700,
        bgcolor: esContado ? FP.color.successSoft : FP.color.warningSoft,
        color: esContado ? FP.color.successDark : '#92400E',
      }}
    />
  );
};

/**
 * Cuadre de caja reutilizable: ventas de contado (`factinv`) + cobros (`pagoscli`)
 * del tenant de finanzas. Recibe credenciales y permisos por props para poder
 * embeberse en otras pantallas (cierre de turno, dashboard, etc.).
 */
export function CuadreCaja({
  servidor,
  idEmpresa,
  usuarioLog,
  claveLog,
  puedeLeer = true,
  nombreNegocio,
  empresa,
  fechaDesdeInicial,
  fechaHastaInicial,
  usuarioInicial = '',
  localidadInicial = '',
  embedded = false,
  titulo = 'Cuadre de caja',
  subtitulo,
  onResultado,
  showError,
}) {
  const [fechaDesde, setFechaDesde] = useState(fechaDesdeInicial || hoyIso);
  const [fechaHasta, setFechaHasta] = useState(fechaHastaInicial || hoyIso);
  const [usuarioCaja, setUsuarioCaja] = useState(usuarioInicial || '');
  const [localidadSel, setLocalidadSel] = useState(OPCION_TODAS_LOCALIDADES);
  const [localidades, setLocalidades] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [consultado, setConsultado] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [detalle, setDetalle] = useState([]);

  const opcionesUsuario = useMemo(
    () => usuarios.map((u) => u.usuario).filter(Boolean),
    [usuarios]
  );

  const opcionesLocalidad = useMemo(
    () => [OPCION_TODAS_LOCALIDADES, ...localidades],
    [localidades]
  );

  useEffect(() => {
    if (!idEmpresa?.db_empresa) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const response = await fetch(`${servidor}/api/productosbackend_obtenerlocalidades`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: idEmpresa,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        });
        if (!response.ok) return;
        const data = await response.json().catch(() => ({}));
        if (cancelado) return;
        const locs = data.localidades || [];
        setLocalidades(locs);
        setLocalidadSel((prev) => {
          if (prev?.id && locs.some((l) => String(l.id) === String(prev.id))) {
            return locs.find((l) => String(l.id) === String(prev.id)) || prev;
          }
          const idIni = String(localidadInicial || '').split('-')[0].trim();
          if (idIni) {
            const encontrada = locs.find((l) => String(l.id) === idIni);
            if (encontrada) return encontrada;
          }
          return OPCION_TODAS_LOCALIDADES;
        });
      } catch {
        if (!cancelado) setLocalidades([]);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [idEmpresa, servidor, usuarioLog, claveLog, localidadInicial]);

  const consultar = useCallback(async () => {
    if (!puedeLeer || !idEmpresa?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(`${servidor}/api/facturabackend_cuadrecaja`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          localidad: localidadSel?.id || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError?.(data.Error || 'No se pudo generar el cuadre de caja');
        setDetalle([]);
        setUsuarios([]);
        setConsultado(false);
        return;
      }
      setUsuarios(data.usuarios || []);
      setDetalle(data.detalle || []);
      setConsultado(true);
      onResultado?.(data);
    } catch {
      showError?.('Error de conexión al consultar el cuadre de caja');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    idEmpresa,
    servidor,
    usuarioLog,
    claveLog,
    fechaDesde,
    fechaHasta,
    localidadSel,
    showError,
    onResultado,
  ]);

  const detalleFiltrado = useMemo(() => {
    const cajero = String(usuarioCaja || '').trim().toLowerCase();
    if (!cajero) return detalle;
    return detalle.filter((r) => String(r.usuario || '').trim().toLowerCase() === cajero);
  }, [detalle, usuarioCaja]);

  const resumen = useMemo(() => {
    const map = {};
    const sumar = (nombre, monto) => {
      const n = Math.round((parseFloat(monto) || 0) * 100) / 100;
      if (!n) return;
      const clave = String(nombre || 'Otros').trim() || 'Otros';
      const k = clave.toLowerCase();
      if (!map[k]) map[k] = { forma_pago: clave, monto: 0, cantidad: 0 };
      map[k].monto = Math.round((map[k].monto + n) * 100) / 100;
      map[k].cantidad += 1;
    };
    detalleFiltrado.forEach((r) => {
      if (r.origen === 'factinv') {
        const ef = parseFloat(r.efectivo) || 0;
        const tc = parseFloat(r.tarjeta) || 0;
        const ot = parseFloat(r.otros) || 0;
        const nc = parseFloat(r.nota_credito) || 0;
        const desglose = ef + tc + ot + nc;
        if (ef) sumar('Efectivo', ef);
        if (tc) sumar('Tarjeta', tc);
        if (nc) sumar('Nota de crédito', nc);
        if (ot) {
          const forma = String(r.forma_pago || '').trim().toLowerCase();
          sumar(forma && !['efectivo', 'tarjeta', 'contado'].includes(forma) ? r.forma_pago : 'Otros', ot);
        }
        if (desglose <= 0) sumar(r.forma_pago || 'Contado', r.monto);
        return;
      }
      sumar(r.forma_pago || 'Cobro CXC', r.monto);
    });
    return Object.values(map).sort((a, b) => b.monto - a.monto);
  }, [detalleFiltrado]);

  const totales = useMemo(() => {
    const ventas = detalleFiltrado
      .filter((r) => r.origen === 'factinv')
      .reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
    const cxc = detalleFiltrado
      .filter((r) => r.origen === 'pagoscli')
      .reduce((s, r) => s + (parseFloat(r.monto) || 0), 0);
    return {
      ventas_contado: Math.round(ventas * 100) / 100,
      cobros_cxc: Math.round(cxc * 100) / 100,
      total_cobrado: Math.round((ventas + cxc) * 100) / 100,
      cantidad_movimientos: detalleFiltrado.length,
    };
  }, [detalleFiltrado]);

  const columnasPrint = useMemo(
    () => [
      { id: 'fecha', label: 'Fecha', render: (_, r) => formatearFecha(r.fecha) },
      { id: 'origen_label', label: 'Origen', render: (_, r) => r.origen_label || r.origen || '' },
      { id: 'usuario', label: 'Usuario' },
      { id: 'nfact', label: 'Documento', render: (_, r) => r.nfact || '—' },
      { id: 'cliente', label: 'Cliente', render: (_, r) => r.cliente || '—' },
      { id: 'forma_pago', label: 'Forma de pago', render: (_, r) => r.forma_pago || '—' },
      { id: 'monto', label: 'Monto', align: 'right', render: (v) => fmtMoney(v) },
    ],
    []
  );

  const columnasFormasPrint = useMemo(
    () => [
      { id: 'forma_pago', label: 'Forma de cobro' },
      { id: 'cantidad', label: 'Movs.', align: 'right' },
      {
        id: 'monto',
        label: 'Monto',
        align: 'right',
        render: (v) => fmtMoney(v),
      },
    ],
    []
  );

  const cuerpoPrintHtml = useMemo(() => {
    const formasConTotal =
      resumen.length === 0
        ? []
        : [
            ...resumen,
            {
              forma_pago: 'Total',
              cantidad: resumen.reduce((s, f) => s + (f.cantidad || 0), 0),
              monto: totales.total_cobrado,
            },
          ];
    return (
      `<p class="print-report-title">Formas de cobro</p>` +
      buildTableHtml(columnasFormasPrint, formasConTotal, 'No hay formas de cobro en el período') +
      `<p class="print-report-title">Detalle de lo cobrado</p>` +
      buildTableHtml(columnasPrint, detalleFiltrado, 'No hay movimientos para imprimir') +
      `<div class="print-footer">Total cobrado RD$ ${fmtMoney(totales.total_cobrado)}</div>`
    );
  }, [columnasFormasPrint, columnasPrint, detalleFiltrado, resumen, totales.total_cobrado]);

  const etiquetaLocalidadPrint = localidadSel?.id
    ? localidadSel.texto || localidadSel.id
    : OPCION_TODAS_LOCALIDADES.texto;

  const subtitlePrint = `Del ${fechaDesde || '…'} al ${fechaHasta || '…'} · ${etiquetaLocalidadPrint}${
    usuarioCaja ? ` · Cajero ${usuarioCaja}` : ''
  }`;

  if (!puedeLeer) {
    const bloqueo = (
      <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, mx: 'auto', textAlign: 'center', p: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 1.6,
            mx: 'auto',
            mb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: FP.color.dangerSoft,
            color: FP.color.danger,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 32 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '1.05rem' }}>
          Acceso denegado
        </Typography>
        <Typography sx={{ color: FP.color.textSecondary, mt: 0.5, fontSize: '0.92rem' }}>
          No tiene permiso del programa «cuadrecajapos» para consultar el cuadre de caja.
        </Typography>
      </Card>
    );
    if (embedded) return bloqueo;
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title={titulo} subtitle={subtitulo} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>{bloqueo}</Box>
      </Box>
    );
  }

  const cuerpo = (
    <Box sx={{ p: embedded ? 0 : { xs: 1.5, sm: 2, md: 3 } }}>
      <Card elevation={0} sx={{ ...cardShellSx, mb: 2 }}>
        <Box sx={filterHeaderSx}>
          <PointOfSaleIcon sx={{ color: FP.color.navy, fontSize: 20 }} />
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
            Filtros del cuadre
          </Typography>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={1.6} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={opcionesUsuario}
                value={usuarioCaja || null}
                onChange={(_e, v) => setUsuarioCaja(v || '')}
                getOptionLabel={(opt) => String(opt || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuario de caja"
                    placeholder="Todos los cajeros"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={opcionesLocalidad}
                value={localidadSel || OPCION_TODAS_LOCALIDADES}
                onChange={(_e, v) => setLocalidadSel(v || OPCION_TODAS_LOCALIDADES)}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.texto || '')}
                isOptionEqualToValue={(a, b) => String(a?.id ?? '') === String(b?.id ?? '')}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Localidad"
                    placeholder="Todas las localidades"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={consultar}
                disabled={cargando || !idEmpresa?.db_empresa}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 1.4,
                  py: 1.25,
                  bgcolor: FP.color.navy,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navy, boxShadow: 'none', filter: 'brightness(0.92)' },
                }}
              >
                Consultar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {consultado && (
        <>
          <Grid container spacing={1.6} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total cobrado"
                value={`RD$ ${fmtMoney(totales.total_cobrado)}`}
                icon={PaymentsIcon}
                accent={FP.color.navy}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Ventas de contado"
                value={`RD$ ${fmtMoney(totales.ventas_contado)}`}
                icon={PointOfSaleIcon}
                accent={FP.color.success}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Cobros CXC"
                value={`RD$ ${fmtMoney(totales.cobros_cxc)}`}
                icon={ReceiptLongIcon}
                accent={FP.color.warning}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Movimientos"
                value={String(totales.cantidad_movimientos || 0)}
                icon={PeopleAltIcon}
                accent={FP.color.textSecondary}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ ...cardShellSx, height: '100%' }}>
                <Box sx={filterHeaderSx}>
                  <PeopleAltIcon sx={{ color: FP.color.navy, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
                    Cajeros del período
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={cellHeadSx}>Usuario</TableCell>
                        <TableCell sx={cellHeadSx} align="right">
                          Cobrado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usuarios.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ py: 3, color: FP.color.textSecondary }}>
                            Nadie registró cobros en este rango.
                          </TableCell>
                        </TableRow>
                      ) : (
                        usuarios.map((u) => (
                          <TableRow
                            key={u.usuario}
                            hover
                            selected={String(usuarioCaja).toLowerCase() === String(u.usuario).toLowerCase()}
                            onClick={() =>
                              setUsuarioCaja((prev) =>
                                String(prev).toLowerCase() === String(u.usuario).toLowerCase() ? '' : u.usuario
                              )
                            }
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.86rem' }}>{u.usuario}</Typography>
                              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.72rem' }}>
                                {u.movimientos} mov. · contado {fmtMoney(u.ventas_contado)} · CXC{' '}
                                {fmtMoney(u.cobros_cxc)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(u.total)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card elevation={0} sx={{ ...cardShellSx, height: '100%' }}>
                <Box sx={{ ...filterHeaderSx, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <PaymentsIcon sx={{ color: FP.color.navy, fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
                      Formas de cobro
                    </Typography>
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={cellHeadSx}>Forma de pago</TableCell>
                        <TableCell sx={cellHeadSx} align="right">
                          Movs.
                        </TableCell>
                        <TableCell sx={cellHeadSx} align="right">
                          Monto
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumen.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ py: 3, color: FP.color.textSecondary }}>
                            No hay formas de pago en el período.
                          </TableCell>
                        </TableRow>
                      ) : (
                        resumen.map((f) => (
                          <TableRow key={f.forma_pago}>
                            <TableCell sx={{ fontWeight: 700 }}>{f.forma_pago}</TableCell>
                            <TableCell align="right">{f.cantidad}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                              {fmtMoney(f.monto)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {resumen.length > 0 && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>
                            {resumen.reduce((s, f) => s + (f.cantidad || 0), 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtMoney(totales.total_cobrado)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={0} sx={{ ...cardShellSx, mt: 2 }}>
            <Box sx={{ ...filterHeaderSx, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <ReceiptLongIcon sx={{ color: FP.color.navy, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.95rem' }}>
                  Detalle de lo cobrado
                </Typography>
              </Box>
              <PrintListado
                title={
                  empresa?.nombre ||
                  (typeof nombreNegocio === 'string' ? nombreNegocio : '') ||
                  'Empresa'
                }
                empresa={empresa}
                reportTitle="Cuadre de caja"
                subtitle={subtitlePrint}
                emptyMessage="No hay movimientos para imprimir"
                printButtonLabel="Imprimir cuadre"
                printButtonProps={{
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navy, boxShadow: 'none', filter: 'brightness(0.92)' },
                  },
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: cuerpoPrintHtml }} />
              </PrintListado>
            </Box>
            {!detalleFiltrado.length ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No hay ventas de contado ni cobros CXC en el rango seleccionado.
                </Alert>
              </Box>
            ) : (
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
                >
                  <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 860, sm: 1080 } }}>
                    <colgroup>
                      {['12%', '14%', '14%', '12%', '18%', '16%', '14%'].map((w, i) => (
                        <col key={i} style={{ width: w }} />
                      ))}
                    </colgroup>
                    <TableBody>
                      <TableRow
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          '& td': {
                            border: 'none',
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadDetalleSx, textAlign: 'center', width: '100%' }}>
                            Fecha
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadDetalleSx}>
                            Origen
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadDetalleSx}>
                            Usuario
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadDetalleSx}>
                            Doc.
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadDetalleSx}>
                            Cliente
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={cellHeadDetalleSx}>
                            Forma de pago
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadDetalleSx, textAlign: 'right', width: '100%' }}>
                            Monto
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {detalleFiltrado.map((r, idx) => {
                        const docLabel = r.nfact || '—';
                        const docExtra = r.tipofactura || r.documento || r.ncf || '';
                        const docTitle = docExtra ? `${docLabel} · ${docExtra}` : String(docLabel);
                        return (
                          <TableRow key={`${r.origen}-${r.nfact}-${r.id_pago || idx}`} hover>
                            <TableCell align="center" sx={cellEllipsisSx} title={formatearFecha(r.fecha)}>
                              {formatearFecha(r.fecha)}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={r.origen_label || r.origen || ''}>
                              {chipOrigen(r.origen)}
                            </TableCell>
                            <TableCell
                              sx={{ ...cellEllipsisSx, fontWeight: 700, color: FP.color.textPrimary }}
                              title={r.usuario || '—'}
                            >
                              {r.usuario || '—'}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={docTitle}>
                              {docLabel}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={r.cliente || '—'}>
                              {r.cliente || '—'}
                            </TableCell>
                            <TableCell sx={cellEllipsisSx} title={r.forma_pago || '—'}>
                              {r.forma_pago || '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              ${fmtMoney(r.monto)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            )}
          </Card>
        </>
      )}
    </Box>
  );

  if (embedded) return cuerpo;

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title={titulo}
        subtitle={
          subtitulo ||
          (nombreNegocio
            ? `${nombreNegocio} · Ventas de contado y cobros CXC`
            : 'Ventas de contado (factinv) y cobros (pagoscli)')
        }
      />
      {cuerpo}
    </Box>
  );
}

/**
 * Pantalla de menú: conecta contexto, permisos de Factura POS y sucursal actual.
 */
const CuadreCajaPage = () => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, claveLog, roleDetalles, nombreNegocio, rnc_empresa, direccion_empresa } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const puedeLeer = useMemo(() => accesoCuadreCajaPos(roleDetalles), [roleDetalles]);

  const empresa = useMemo(
    () => ({
      nombre:
        (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
          ? nombreNegocio.trim()
          : '') ||
        selectedCompany?.empresa ||
        selectedCompany?.name ||
        'Empresa',
      rnc: rnc_empresa || '',
      direccion: direccion_empresa || '',
    }),
    [nombreNegocio, selectedCompany, rnc_empresa, direccion_empresa]
  );

  return (
    <CuadreCaja
      servidor={servidor}
      idEmpresa={selectedCompany}
      usuarioLog={usuarioLog}
      claveLog={claveLog}
      puedeLeer={puedeLeer}
      nombreNegocio={nombreNegocio}
      empresa={empresa}
      showError={showError}
    />
  );
};

export default CuadreCajaPage;
